import { getDatasetInfo, mergeDatasetInfoExtras } from '../offline-features/getDatasetInfo.js';
import { getDatasets } from '../offline-features/getDatasets.js';
import { setDatasetInfo, setDatasets } from '../context/datasets.js';
import { openDataset, closeDataset } from '../context/openDatasets.js';
import Utils from './Utils.jsx';
import { applyTimescale, utcDay } from './timescale.js';

export const sourceFormatLabel = dataset => (
	dataset?.SourceFormat === 'mspdi' ? 'MSP' : 'P6'
);

export const dispatchDatasetInfo = (dispatch, datasetId, result, datasets) => {
	const id = String(datasetId);
	dispatch(setDatasetInfo(
		id,
		result.wbsTree,
		result.activities,
		result.activitiesMap,
		result.activityCodesRelativesMap,
		Array.isArray(result.activityCodesTree) ? null : result.activityCodesTree,
		result.activityCodesTreeSequence,
		result.layouts,
		result.Id || null,
		result.ViewType || 'WBS List',
		result.GroupBy || null,
		result.GroupByActivityCode || null,
		result.MapGroupBy || null
	));
	const listed = (datasets && (datasets[id] || datasets[Number(id)])) || {};
	// A direct link has no library row yet. Keep enough fields to render if the
	// list lookup misses, instead of a blank page on the first paint.
	const base = {
		Id: id,
		Name: listed.Name || '',
		HoursPerDay: listed.HoursPerDay || 8,
		RemainingDurationUnit: listed.RemainingDurationUnit || 'days',
		Permissions: listed.Permissions && typeof listed.Permissions === 'object' ? listed.Permissions : {},
		projects: listed.projects && !Array.isArray(listed.projects) ? listed.projects : {},
		filterOptions: listed.filterOptions || {
			flags: [],
			resources: [],
			activityCodesHashMap: {},
			activityCodeTypeActivityCodes: {}
		},
		...listed
	};
	const patched = applyTimescale(base, result.activities);
	const merged = mergeDatasetInfoExtras(patched.dataset, result);
	dispatch(setDatasets({
		...(datasets || {}),
		[id]: merged
	}));
	if (patched.persist) {
		const startDate = utcDay(patched.start);
		const endDate = utcDay(patched.end);
		if (startDate) {
			Utils.ajax('updateStartDate', { dataset: id, startDate }, dispatch);
		}
		if (endDate) {
			Utils.ajax('updateEndDate', { dataset: id, endDate }, dispatch);
		}
	}
};

let projectLoadGeneration = 0;

/** Fresh project list, then the activity payload, with one timescale for both paths. */
export const loadFreshProject = ({ dispatch, datasetId }) => {
	const id = String(datasetId);
	const generation = ++projectLoadGeneration;
	dispatch(openDataset(id));
	return getDatasets(dispatch).catch(() => ({ success: false, datasets: {} })).then(list => {
		if (generation !== projectLoadGeneration) {
			return { success: false, stale: true };
		}
		const datasets = (list && list.success && list.datasets) ? list.datasets : {};
		return getDatasetInfo(dispatch, id).then(result => {
			if (generation !== projectLoadGeneration) {
				return { success: false, stale: true };
			}
			if (result && result.success) {
				dispatchDatasetInfo(dispatch, id, result, datasets);
			}
			return result;
		});
	});
};

export const focusProject = ({ dispatch, navigate, datasetId }) => {
	const id = String(datasetId);
	dispatch(openDataset(id));
	if (typeof navigate === 'function') {
		navigate(`/app/dataset/${id}`);
	}
	return loadFreshProject({ dispatch, datasetId: id });
};

export const closeOpenProject = ({
	dispatch,
	navigate,
	datasetId,
	focusedDatasetId,
	redirectIfFocused = true
}) => {
	const id = String(datasetId);
	projectLoadGeneration += 1;
	dispatch(closeDataset(id));
	if (String(focusedDatasetId) !== id) {
		return [];
	}
	dispatch(setDatasetInfo(null));
	if (redirectIfFocused && typeof navigate === 'function') {
		navigate('/app/');
	}
	return [];
};
